import {
  IProjectedPipeline,
  ProjectedPipeline,
} from '@app/models/ProjectedPipeline'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { Map, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface IProjectedProfitState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: Map<string, IProjectedPipeline[]>
  readonly totalItems: number
}

const ProjectedProfitStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: Map<string, IProjectedPipeline[]>(),
  totalItems: 0,
})

export class ProjectedProfitState extends ProjectedProfitStateRecord
  implements IProjectedProfitState {
  public static fromJS(initialState?: Partial<IProjectedProfitState>) {
    if (!initialState) {
      return new ProjectedProfitState()
    }

    const { items, ...rest } = initialState
    return new ProjectedProfitState({
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: Map<string, ProjectedPipeline[]>
  public readonly totalItems: number

  constructor(props?: Partial<ProjectedProfitState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ProjectedProfitActionTypes {
  LoadProjectedProfit = 'PROJECTED_PROFIT_REPORT::LOAD_PROJECTED_PROFIT',
  LoadProjectedProfitFailure = 'PROJECTED_PROFIT_REPORT::LOAD_PROJECTED_PROFIT_FAILURE',
  LoadProjectedProfitRequest = 'PROJECTED_PROFIT_REPORT::LOAD_PROJECTED_PROFIT_REQUEST',
  LoadProjectedProfitSuccess = 'PROJECTED_PROFIT_REPORT::LOAD_PROJECTED_PROFIT_SUCCESS',
  SetFilterText = 'PROJECTED_PROFIT_REPORT::SET_FILTER_TEXT',
}

interface ILoadProjectedProfitAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ProjectedProfitActionTypes.LoadProjectedProfit
}

interface ILoadProjectedProfitFailureAction {
  error: string
  type: ProjectedProfitActionTypes.LoadProjectedProfitFailure
}

interface ILoadProjectedProfitRequestAction {
  type: ProjectedProfitActionTypes.LoadProjectedProfitRequest
}

interface ILoadProjectedProfitSuccessAction {
  append: boolean
  items: Map<string, ProjectedPipeline[]>
  totalItems: number
  type: ProjectedProfitActionTypes.LoadProjectedProfitSuccess
}

interface ISetFilterTextAction {
  filter: string
  type: ProjectedProfitActionTypes.SetFilterText
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadProjectedProfitFailureAction
  | ILoadProjectedProfitRequestAction
  | ILoadProjectedProfitSuccessAction
  | ISetFilterTextAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadProjectedProfit(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadProjectedProfitAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ProjectedProfitActionTypes.LoadProjectedProfit,
  }
}

function loadProjectedProfitFailure(
  error: string,
): ILoadProjectedProfitFailureAction {
  return {
    error,
    type: ProjectedProfitActionTypes.LoadProjectedProfitFailure,
  }
}

function loadProjectedProfitRequest(): ILoadProjectedProfitRequestAction {
  return {
    type: ProjectedProfitActionTypes.LoadProjectedProfitRequest,
  }
}

function loadProjectedProfitSuccess(
  items: Map<string, ProjectedPipeline[]>,
  append: boolean,
  totalItems: number,
): ILoadProjectedProfitSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ProjectedProfitActionTypes.LoadProjectedProfitSuccess,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: ProjectedProfitActionTypes.SetFilterText,
  }
}

export const actionCreators = {
  loadProjectedProfit,
  setFilterText,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ProjectedProfitState> = (
  state: ProjectedProfitState = new ProjectedProfitState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ProjectedProfitActionTypes.LoadProjectedProfitFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as ProjectedProfitState

    case ProjectedProfitActionTypes.LoadProjectedProfitRequest:
      return state.set('isLoading', true) as ProjectedProfitState

    case ProjectedProfitActionTypes.LoadProjectedProfitSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', action.items)
        .set('totalItems', action.totalItems) as ProjectedProfitState

    case ProjectedProfitActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as ProjectedProfitState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

function reducePipelineData(pipeline: ProjectedPipeline[]) {
  const result = {} as any
  let prop
  let propList = []

  for (const item of pipeline) {
    prop = `${item.year}${item.month}`
    propList.push(prop)
  }

  propList = propList.sort((a, b) => {
    if (parseInt(a) < parseInt(b)) {
      return -1
    }

    return 1
  })

  for (let i = propList.length - 1; i >= 0; i--) {
    for (const item of pipeline) {
      if (propList[i] === `${item.year}${item.month}`) {
        prop = `${item.year}${item.month}`

        if (!result[prop]) {
          result[prop] = []
        }

        result[prop].push(item)
      }
    }

    const toRemove = propList.pop()
    propList = propList.filter((item) => {
      return item !== toRemove
    })

    i = propList.length
  }

  return Map<string, ProjectedPipeline[]>(result)
}

// -------------------------
// SAGAS
// -------------------------
function* loadProjectedProfitSaga(action: ILoadProjectedProfitAction) {
  try {
    yield put(loadProjectedProfitRequest())

    const currentState: ProjectedProfitState = yield select(
      (state: ApplicationState) => state.projectedProfit,
    )

    const query: string[] = []

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/BrokeragePipeLines/ProjectedProfit'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: ProjectedPipeline[] = result.results.map((c: any) =>
        ProjectedPipeline.fromJS(c),
      )
      const r = reducePipelineData(contacts)
      yield put(loadProjectedProfitSuccess(r, false, result.count))
    } else {
      yield put(loadProjectedProfitFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadProjectedProfitFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadProjectedProfit() {
  yield takeLatest(
    ProjectedProfitActionTypes.LoadProjectedProfit,
    loadProjectedProfitSaga,
  )
}

export function* projectedProfitRootSaga() {
  yield fork(watchLoadProjectedProfit)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): ProjectedProfitState => state.projectedProfit

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
